<template>
  <validation-observer v-slot="{ passes, handleSubmit }">
    <b-modal
      :id="modalId"
      size="sm"
      @ok.prevent="passes(() => updateContact())"
      @hidden="onHidden"
    >
      <h6 slot="modal-header" class="modal-title">Update contact</h6>

      <b-container fluid>
        <b-form role="form" @submit.prevent="handleSubmit(updateContact)">
          <base-input
            alternative
            class="mb-3"
            :label="model.isCompany ? 'Company name' : 'Full name'"
            name="Name"
            :rules="{ required: true }"
            :debounce="500"
            :prepend-icon="model.isCompany ? 'fa fa-building' : 'fa fa-user'"
            placeholder="Name"
            v-model="model.name"
          ></base-input>

          <b-row class="mb-2">
            <b-col cols="8">
              <label class="form-control-label align-bottom"
                >Is this contact a company?</label
              >
            </b-col>
            <b-col cols="4" class="text-right">
              <base-switch class="mr-1" v-model="model.isCompany"></base-switch>
            </b-col>
          </b-row>

          <base-input
            v-if="!model.isCompany"
            alternative
            label="Previous name(s)"
            class="mb-3"
            name="Previous name(s)"
            :rules="{ required: false }"
            :debounce="500"
            prepend-icon="fa fa-address-card"
            placeholder="Previous name"
            v-model="model.previousName"
          ></base-input>

          <base-input
            class="mb-3"
            :label="
              model.isCompany ? 'Registered address' : 'Residential address'
            "
            alternative
          >
            <!-- TODO: make this required -->
            <address-autocomplete-input
              v-model="model.address"
              placeholder="e.g. 12 Marine Drive, Douglas, IM8 XE1"
            ></address-autocomplete-input>
          </base-input>

          <base-input
            v-if="!model.isCompany"
            alternative
            class="mb-3"
            label="Occupation"
            name="Occupation"
            :rules="{ required: !model.isCompany }"
            :debounce="500"
            prepend-icon="fa fa-wrench"
            placeholder="Occupation"
            v-model="model.occupation"
          ></base-input>

          <base-input
            class="mb-3"
            label="Nationality"
            v-if="!model.isCompany"
            alternative
          >
            <el-select
              v-model="model.nationality"
              filterable
              placeholder="Nationality"
              default-first-option
            >
              <el-option
                v-for="(nationality, code) in nationalities"
                :key="code"
                :label="nationality"
                :value="code"
              ></el-option>
            </el-select>
          </base-input>
        </b-form>
      </b-container>

      <span slot="modal-ok">
        <span v-if="!updating">
          Update contact
          <i class="fa fa-arrow-right ml-2"></i>
        </span>
        <span v-else>
          <i class="fa fa-spin fa-spinner mr-2"></i> Please wait&hellip;
        </span>
      </span>
    </b-modal>
  </validation-observer>
</template>

<script>
import { BaseAlert } from "@/components";
import { get } from "dot-prop";
import nationalities from "i18n-nationality";
import { Select, Option } from "element-ui";
import AddressAutocompleteInput from "../Forms/AddressAutocompleteInput";

nationalities.registerLocale(require("i18n-nationality/langs/en.json"));

export default {
  name: "contact-edit-form",
  props: {
    accountId: {
      type: String,
      required: true,
    },
    contact: {
      type: Object,
      required: true,
      description: "Contact",
    },
    modalId: {
      type: String,
      required: true,
      description: "Modal ID",
    },
  },
  components: {
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    AddressAutocompleteInput,
  },
  beforeMount() {
    // set model from contact prop
    this.model.name = this.contact.name;
    this.model.previousName = this.contact.previousName;
    this.model.address = this.contact.address;
    this.model.occupation = this.contact.occupation;
    this.model.nationality = this.contact.nationalityCountryCode;
    this.model.isCompany = this.contact.isCompany;
  },
  data() {
    return {
      model: {
        name: null,
        previousName: null,
        address: null,
        occupation: null,
        nationality: "GB",
        isCompany: false,
      },
      updating: false,
    };
  },
  computed: {
    nationalities() {
      return nationalities.getNames("en");
    },
  },
  methods: {
    onHidden() {
      this.$emit("hidden");
    },
    updateContact() {
      this.updating = true;
      const requestBody = {
        name: this.model.name,
        previousName: this.model.previousName,
        address: this.model.address,
        occupation: this.model.occupation,
        nationalityCountryCode: this.model.nationality,
        isCompany: this.model.isCompany,
      };

      // omit person fields for company
      if (requestBody.isCompany) {
        delete requestBody.occupation;
        delete requestBody.nationalityCountryCode;
      }

      this.axios
        .patch(
          `/v1/accounts/${this.accountId}/contacts/${this.contact.id}`,
          requestBody
        )
        .then((response) => {
          this.updating = false;
          if (response.data) {
            const contact = response.data;
            this.$emit("update-contact", contact);
          }

          // notify (contact updated)
          this.$notify({
            message: "Contact updated",
            timeout: 3000,
            icon: "fa fa-check",
            type: "success",
          });

          // close modal
          this.$nextTick(() => {
            this.$bvModal.hide(this.modalId);
          });
        })
        .catch((error) => {
          this.updating = false;
          this.errorMessage = "Failed to update contact";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
  },
};
</script>
